import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ClientError } from 'graphql-request';
import type { BankAccountAccount } from '@/lib/payments/bankAccountAccount.types';
import type { AddBankAccountInput } from '@/lib/payments/paymentAccount.types';
import {
  type MyMemberInfo,
  mapToAutoPaySummary,
  memberInfoKey,
  requestEnableMyAutoPay,
} from '../memberInfo/memberInfoApi';
import {
  promoteDefault,
  type SavedPaymentMethodsData,
  savedPaymentMethodsKey,
  toBillingAddressInput,
} from '../paymentApi';
import {
  BANK_ACCOUNT_TYPE_TO_GQL,
  PaymentAddBankAccountError,
  PaymentVerifyAchError,
  requestCreateBankProfile,
  requestVerifyAch,
} from './bankAccountApi';

interface UseAddBankAccountOptions {
  onSuccess?: () => void;
}

/**
 * Submits a new bank account via the two-step gateway flow: verify ACH (account/routing → token)
 * then create the profile (token + last6 only). The full account number never reaches create.
 */
export function useAddBankAccount({ onSuccess }: UseAddBankAccountOptions = {}) {
  const { toast } = useToast();
  const tError = useErrorTranslation();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: AddBankAccountInput) => {
      const verified = await requestVerifyAch({
        accountNumber: input.accountNumber,
        routingNumber: input.routingNumber,
      });

      const profile = await requestCreateBankProfile({
        gatewayTokenId: verified.gatewayTokenId,
        authTransactionId: verified.authTransactionId,
        bankName: input.bankName,
        // Backend requires 5-6 digits; always exactly 6 here because makeBankSchema enforces an
        // 8-digit account-number floor. Raising that floor toward the backend's 4-digit minimum
        // would need this to change too.
        last6: input.accountNumber.slice(-6),
        routingLast9: input.routingNumber,
        accountType: BANK_ACCOUNT_TYPE_TO_GQL[input.accountType],
        nameOnAccount: input.billing.name,
        hsa: input.isHsa,
        isDefault: input.makeDefault,
        billingAddress: toBillingAddressInput(input.billing),
      });

      // Skip the call entirely when we already know (from the same cache that locks the checkbox)
      // that the member is enrolled — avoids a redundant round trip and Salesforce read. The
      // backend's own idempotent pre-check remains the fallback if this cache is stale.
      const cachedMemberInfo = queryClient.getQueryData<MyMemberInfo>(memberInfoKey);
      const alreadyEnrolled = cachedMemberInfo ? mapToAutoPaySummary(cachedMemberInfo).autoPayEnrolled : false;
      if (input.autoPay && !alreadyEnrolled) {
        // Enrollment failure must not block the bank-account save — swallow and track only.
        try {
          await requestEnableMyAutoPay();
        } catch (err) {
          if (err instanceof Error) {
            trackException(err, { source: 'enableMyAutoPay', context: 'addBankAccount' });
          }
        }
      }

      // Echo of what we just submitted — every field here is data we already sent, not a guess.
      // Lets the list show the new account immediately instead of waiting on the
      // invalidate-triggered refetch (a live Salesforce round trip that can take a few seconds).
      const newAccount: BankAccountAccount = {
        id: profile.id,
        last6: input.accountNumber.slice(-6),
        bankName: input.bankName,
        accountType: input.accountType,
        holderName: input.billing.name,
        line1: input.billing.line1,
        line2: input.billing.line2,
        city: input.billing.city,
        state: input.billing.state,
        zip: input.billing.zip,
        isDefault: input.makeDefault,
        isHsa: input.isHsa,
        // A freshly created profile is always active in Salesforce.
        isActive: true,
      };
      return newAccount;
    },
    onSuccess: (newAccount) => {
      queryClient.setQueryData<SavedPaymentMethodsData>(savedPaymentMethodsKey, (existing) => {
        const withNewAccount: SavedPaymentMethodsData = {
          cards: existing?.cards ?? [],
          bankAccounts: [...(existing?.bankAccounts ?? []), newAccount],
        };
        // The new default (if any) atomically demotes the prior one server-side, across both cards
        // and bank accounts — mirror that here so the optimistic list doesn't show a stale badge
        // anywhere until the refetch reconciles it.
        return newAccount.isDefault ? promoteDefault(withNewAccount, newAccount.id) : withNewAccount;
      });
      void queryClient.invalidateQueries({ queryKey: memberInfoKey });
      void queryClient.invalidateQueries({ queryKey: savedPaymentMethodsKey });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      // PaymentVerifyAchError wraps domain Failure variants (DECLINED, UNSUPPORTED_ACCOUNT);
      // wire value is kept for telemetry, prefixed to PAYMENT_VERIFY_ACH_* for i18n key lookup.
      // PaymentAddBankAccountError wraps addBankAccount's Failure variant — no real codes exist yet
      // (RESERVED placeholder), so it falls through to the generic UNKNOWN message.
      const isDomainError = error instanceof PaymentVerifyAchError || error instanceof PaymentAddBankAccountError;
      const code = isDomainError ? error.code : extractGqlErrorCode(error);
      const errorKey = error instanceof PaymentVerifyAchError ? `PAYMENT_VERIFY_ACH_${code}` : code;
      if (error instanceof Error) {
        // ClientError.message serializes request variables (routingNumber/accountNumber on the
        // verify step). Track a sanitized error to keep that out of Application Insights.
        const trackable = error instanceof ClientError ? new Error(`addBankAccount: ${code}`) : error;
        trackException(trackable, { source: 'addBankAccount', errorCode: code });
      }
      toast({ title: tError('title'), description: tError(errorKey), variant: 'destructive' });
    },
  });

  return { submit: (input: AddBankAccountInput) => mutation.mutate(input), isPending: mutation.isPending };
}
